import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../config';
import {
  addInteractionDictionary,
  deleteInteractionDictionary,
  editInteractionDictionary,
  getInteractionDictionary,
  getInteractionsDictionary,
} from './methods';

const useGetInteractionsDictionaryQuery = () => {
  const { isPending, isSuccess, isError, data } = useQuery({
    queryFn: getInteractionsDictionary,
    queryKey: [queryKeys.interactionsDictionaryKey],
    staleTime: Infinity,
  });

  return { data, isError, isPending, isSuccess };
};

const useGetInteractionDictionaryQuery = (id: number) => {
  const { isPending, isSuccess, isError, data } = useQuery({
    enabled: !!id,
    queryFn: () => getInteractionDictionary(id),
    queryKey: [queryKeys.interactionsDictionaryKey, id],
  });

  return { data, isError, isPending, isSuccess };
};

const useAddInteractionDictionaryMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: addInteractionDictionaryMutation,
  } = useMutation({
    mutationFn: addInteractionDictionary,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.interactionsDictionaryKey],
      });
    },
  });

  return { addInteractionDictionaryMutation, isError, isPending, isSuccess };
};

const useEditInteractionDictionaryMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: editInteractionDictionaryMutation,
  } = useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      editInteractionDictionary(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.interactionsDictionaryKey],
      });
    },
  });

  return { editInteractionDictionaryMutation, isError, isPending, isSuccess };
};

const useDeleteInteractionDictionaryMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: deleteInteractionDictionaryMutation,
  } = useMutation({
    mutationFn: deleteInteractionDictionary,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.interactionsDictionaryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.themesKey],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.interactionsKey],
      });
    },
  });

  return { deleteInteractionDictionaryMutation, isError, isPending, isSuccess };
};

export {
  useAddInteractionDictionaryMutation,
  useDeleteInteractionDictionaryMutation,
  useEditInteractionDictionaryMutation,
  useGetInteractionDictionaryQuery,
  useGetInteractionsDictionaryQuery,
};
